/**
 * Sysadmin System Library — Unified Documents (Library-PR-X3 / Phase 4).
 *
 * Thin wrapper around <LibraryDocumentsView scope="sysadmin"> — the
 * shared component owns the unified table + filter row + bulk action
 * bar + "New" dropdown. This file owns the dialog state (Upload,
 * SystemArticleEditorDialog, DocumentEditorDialog, Unpublish) that
 * sits AROUND the shared view.
 *
 * Pre-X3 this file was 580 LOC of inline table + dialog wiring. Post-
 * X3 it's ~200 LOC: one useState per dialog + the adapter callback
 * map. The shared <LibraryDocumentsView> is the single source of truth
 * for both tenant and sysadmin list shells.
 *
 * Backing store (organization_id IS NULL): ai_documents — files and, since
 * decision 19 (c41), articles (kind 'article', body and versions in child
 * tables) — and the one folder store, ai_document_folders (decision 18).
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  useSysLibFolders, useUpdateSysLibDocument,
  useUnpublishSysLibArticle, useUnpublishSysLibDocument,
  useUploadSysLibDocument,
  type SysLibArticle, type SysLibDocument,
} from '@/lib/use-sysadmin-library';
import { getApiErrorMessage } from '@/lib/api-error';
import { LibraryUnpublishDialog } from '@/components/sysadmin/library-lifecycle';
import { UploadDialog } from '@/components/library/upload-dialog';
import { SystemArticleEditorDialog } from '@/components/sysadmin/system-article-editor-dialog';
import {
  LibraryDocumentsView,
  useSysadminAdapter,
} from '@/components/library-documents-shared';
import { formatBytes } from '@/components/library-documents-shared/LibraryDocumentsView.helpers';

export const Route = createFileRoute('/sysadmin/library/documents')({
  component: SysLibDocumentsPage,
});

function SysLibDocumentsPage() {
  // ── Dialog state ──────────────────────────────────────────────────────────
  const [showUpload, setShowUpload] = useState(false);
  const [showCreateArticle, setShowCreateArticle] = useState(false);
  const [editingArticle, setEditingArticle] = useState<SysLibArticle | null>(null);
  const [editingDocument, setEditingDocument] = useState<SysLibDocument | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<
    | { kind: 'article'; row: SysLibArticle }
    | { kind: 'uploaded'; row: SysLibDocument }
    | null
  >(null);

  // ── Data needed only for dialog props ─────────────────────────────────────
  // Decision 18 — the System Library's ONE folder set, for every dialog.
  const foldersQ = useSysLibFolders();

  // ── Mutations ─────────────────────────────────────────────────────────────
  const uploadMut = useUploadSysLibDocument();
  const unpublishArticleMut = useUnpublishSysLibArticle();
  const unpublishDocumentMut = useUnpublishSysLibDocument();

  // ── Wire shared view via adapter ──────────────────────────────────────────
  const adapter = useSysadminAdapter({
    onOpenCreateArticle: () => setShowCreateArticle(true),
    onEditArticle: setEditingArticle,
    onEditDocument: setEditingDocument,
    onOpenUpload: () => setShowUpload(true),
    onUnpublishArticle: (article) =>
      setUnpublishTarget({ kind: 'article', row: article }),
    onUnpublishDocument: (doc) =>
      setUnpublishTarget({ kind: 'uploaded', row: doc }),
  });

  return (
    <>
      <LibraryDocumentsView scope="sysadmin" adapter={adapter} />

      {/* Upload dialog */}
      <UploadDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        title={adapter.uploadDialogTitle}
        description={adapter.uploadDialogDescription}
        folderScope="system"
        uploader={async (formData) => {
          await uploadMut.mutateAsync(formData);
        }}
        onUploaded={() => {
          // useUploadSysLibDocument already invalidates the documents
          // list cache. No-op here; the dialog's success toast renders.
        }}
      />

      {/* Article create + edit dialogs */}
      <SystemArticleEditorDialog
        open={showCreateArticle}
        onOpenChange={setShowCreateArticle}
        article={null}
        folders={foldersQ.data?.items ?? []}
      />
      <SystemArticleEditorDialog
        open={editingArticle !== null}
        onOpenChange={(o) => {
          if (!o) setEditingArticle(null);
        }}
        article={editingArticle}
        folders={foldersQ.data?.items ?? []}
      />

      {/* Document metadata edit dialog */}
      <DocumentEditorDialog
        open={editingDocument !== null}
        onOpenChange={(o) => !o && setEditingDocument(null)}
        document={editingDocument}
        folders={foldersQ.data?.items ?? []}
      />

      {/* Unpublish dialog (handles both kinds) */}
      {unpublishTarget && (
        <LibraryUnpublishDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) setUnpublishTarget(null);
          }}
          resourceLabel={
            unpublishTarget.kind === 'article'
              ? `article "${unpublishTarget.row.title}"`
              : `document "${unpublishTarget.row.title}"`
          }
          busy={
            unpublishTarget.kind === 'article'
              ? unpublishArticleMut.isPending
              : unpublishDocumentMut.isPending
          }
          onConfirm={async ({ reason, nextStatus }) => {
            if (unpublishTarget.kind === 'article') {
              await unpublishArticleMut.mutateAsync({ id: unpublishTarget.row.id, reason, nextStatus });
            } else {
              await unpublishDocumentMut.mutateAsync({ id: unpublishTarget.row.id, reason, nextStatus });
            }
            toast.success(`Unpublished — now ${nextStatus}`);
            setUnpublishTarget(null);
          }}
        />
      )}
    </>
  );
}

// ─── Document metadata editor (inline, simple) ──────────────────────────────
//
// Stays in this file because the metadata fields (title / description /
// folder) are sysadmin-scope-specific and won't be reused elsewhere. The
// SystemArticleEditorDialog (Tiptap editor) was lifted to its own file
// because the create AND edit paths both need it.

function DocumentEditorDialog({
  open, onOpenChange, document: doc, folders,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  document: SysLibDocument | null;
  folders: { id: string; name: string }[];
}) {
  const [title, setTitle] = useState(doc?.title ?? '');
  const [description, setDescription] = useState(doc?.description ?? '');
  const [folderId, setFolderId] = useState<string | null>(doc?.folderId ?? null);
  const [submitting, setSubmitting] = useState(false);

  useMemo(() => {
    if (open && doc) {
      setTitle(doc.title);
      setDescription(doc.description ?? '');
      setFolderId(doc.folderId);
    }
  }, [open, doc]);

  const updateMut = useUpdateSysLibDocument(doc?.id ?? '');

  const submit = async () => {
    if (!title.trim() || !doc) return;
    setSubmitting(true);
    try {
      await updateMut.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        folderId,
      });
      toast.success('Document updated');
      onOpenChange(false);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit System Document</DialogTitle>
          <DialogDescription>
            Update title, description, or folder. The underlying file is immutable.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>File</Label>
            <div className="text-xs text-muted-foreground">
              {doc.fileName} ({formatBytes(doc.fileSize)})
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={500} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">Description</Label>
            <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="folder">Folder</Label>
            <Select
              value={folderId ?? '__root__'}
              onValueChange={(v) => setFolderId(v === '__root__' ? null : v)}
            >
              <SelectTrigger id="folder">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__root__">Root</SelectItem>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || !title.trim()}>
            {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
